import React, {useState} from 'react'
import {
  Typography,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  DialogFormContainer,
  FormRow,
  FormField,
  DialogDividerRow,
  DialogDividerLine,
  DialogDividerText,
  DialogCancelButton,
  DialogConfirmButton,
  FieldHelperText,
} from './styled'
import {DELIVERY_TYPES, DISPATCH_MODES, PAYMENT_MODES, QC_TYPES, SHIPMENT_TYPES} from './constants'

const AddRuleDialog = ({
  open,
  onClose,
  newRuleForm,
  onFormChange,
  copyFromRule,
  onCopyFromChange,
  rules,
  onAddRule,
}) => {
  const [copyingFromRule, setCopyingFromRule] = useState(false)

  const onCopyFromChangeHandler = (ruleId) => {
    setCopyingFromRule(!!ruleId)
    onCopyFromChange(ruleId)
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <StyledDialogTitle>Add New Rule</StyledDialogTitle>
      <StyledDialogContent>
        <DialogFormContainer>
          <Typography variant='body2' color='text.secondary'>
            Select the delivery type, dispatch mode, and payment mode for your new rule. Optionally
            copy settings from an existing rule.
          </Typography>

          <FormRow>
            <FormField disabled={!!newRuleForm.deliveryType && !copyingFromRule}>
              <InputLabel>Copy from existing rule (optional)</InputLabel>
              <Select
                value={copyFromRule}
                onChange={(e) => onCopyFromChangeHandler(e.target.value)}
                label='Copy from existing rule (optional)'
              >
                <MenuItem value=''>
                  <em>Create new rule from scratch</em>
                </MenuItem>
                {rules.map((rule) => (
                  <MenuItem key={rule.id} value={rule.id.toString()}>
                    {rule.name}
                  </MenuItem>
                ))}
              </Select>
              <FieldHelperText>
                Copy all settings (auto allocation, fallback couriers, advanced settings) from an
                existing rule
              </FieldHelperText>
            </FormField>
          </FormRow>

          <FormRow>
            <DialogDividerRow>
              <DialogDividerLine />
              <DialogDividerText>OR</DialogDividerText>
              <DialogDividerLine />
            </DialogDividerRow>
          </FormRow>

          <FormRow>
            <FormField disabled={copyingFromRule}>
              <InputLabel>Delivery Type</InputLabel>
              <Select
                value={newRuleForm.deliveryType}
                onChange={(e) => onFormChange('deliveryType', e.target.value)}
                label='Delivery Type'
              >
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                {DELIVERY_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box display='flex' alignItems='center' gap={1}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FieldHelperText>
                Forward: Regular delivery to customer | Reverse: Return pickup from customer
              </FieldHelperText>
            </FormField>
          </FormRow>

          {newRuleForm.deliveryType && (
            <FormRow>
              <FormField disabled={copyingFromRule || newRuleForm.deliveryType === 'RVP'}>
                <InputLabel>Shipment Type</InputLabel>
                <Select
                  value={newRuleForm.deliveryType === 'RVP' ? 'B2C' : newRuleForm.shipmentType}
                  onChange={(e) => onFormChange('shipmentType', e.target.value)}
                  label='Shipment Type'
                >
                  {SHIPMENT_TYPES.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
                <FieldHelperText>
                  Select B2B for business-to-business or B2C for business-to-consumer shipments
                </FieldHelperText>
              </FormField>
            </FormRow>
          )}

          {newRuleForm.deliveryType === 'FORWARD' && newRuleForm.shipmentType && (
            <FormRow>
              <FormField disabled={copyingFromRule}>
                <InputLabel>Dispatch Mode</InputLabel>
                <Select
                  value={newRuleForm.dispatchMode}
                  onChange={(e) => onFormChange('dispatchMode', e.target.value)}
                  label='Dispatch Mode'
                >
                  <MenuItem value=''>
                    <em>Both</em>
                  </MenuItem>
                  {DISPATCH_MODES.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
                <FieldHelperText>
                  Surface: Ground transport (slower, cheaper) | Air: Air transport (faster,
                  expensive)
                </FieldHelperText>
              </FormField>
            </FormRow>
          )}

          {newRuleForm.deliveryType === 'FORWARD' && newRuleForm.shipmentType && (
            <FormRow>
              <FormField disabled={copyingFromRule}>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={newRuleForm.paymentMode}
                  onChange={(e) => onFormChange('paymentMode', e.target.value)}
                  label='Payment Mode'
                >
                  <MenuItem value=''>
                    <em>All</em>
                  </MenuItem>
                  {PAYMENT_MODES.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
                <FieldHelperText>
                  COD: Cash on Delivery | Prepaid: Payment made in advance
                </FieldHelperText>
              </FormField>
            </FormRow>
          )}

          {newRuleForm.deliveryType === 'RVP' && (
            <FormRow>
              <FormField disabled={copyingFromRule}>
                <InputLabel>QC Type *</InputLabel>
                <Select
                  value={newRuleForm.qcType}
                  onChange={(e) => onFormChange('qcType', e.target.value)}
                  label='QC Type *'
                  required
                >
                  {QC_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                <FieldHelperText>
                  QC: Quality Check enabled (doorstep couriers) | Non-QC: Quality Check not enabled
                </FieldHelperText>
              </FormField>
            </FormRow>
          )}
        </DialogFormContainer>
      </StyledDialogContent>
      <StyledDialogActions>
        <DialogCancelButton onClick={onClose}>Cancel</DialogCancelButton>
        <DialogConfirmButton
          onClick={() => {
            setCopyingFromRule(false)
            onAddRule()
          }}
          variant='contained'
          disabled={
            (newRuleForm.deliveryType === 'RVP' && !newRuleForm.qcType) || !newRuleForm.deliveryType
          }
        >
          Add Rule
        </DialogConfirmButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}

export default AddRuleDialog
